import ApiService from '@/core/services/api.service';
import { cmdUrl } from '@/utils/apiUrl';

/**
 * InstallmentRepository - Handles all installment-related API operations
 * Following the established repository pattern for consistent API management
 */
export default {
  /**
   * Get all installments with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number for pagination
   * @param {number} params.pageSize - Number of items per page
   * @param {string} params.keyword - Search keyword
   * @returns {Promise<Object>} API response with installment data
   */
  async getAll(params = {}) {
    try {
      const { page = 1, pageSize = 1000, keyword = '' } = params;
      const response = await ApiService.query(cmdUrl.InstallmentsUrl.root, {
        params: { page, pageSize, keyword },
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching installments:', error);
      throw error;
    }
  },

  /**
   * Get installments for autocomplete (simplified format)
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} Array of installment options for autocomplete
   */
  async getForAutocomplete(params = {}) {
    try {
      const response = await this.getAll({ ...params, pageSize: 1000 });
      const installments = response.result || response.data || [];
      
      // Transform to autocomplete format
      const options = installments.map((item) => ({
        id: item.id,
        name: item.name,
        suggestionName: item.name,
        code: item.code,
      }));

      // Add "All services" option at the beginning
      options.unshift({
        id: null,
        name: 'Tất cả dịch vụ',
        suggestionName: 'Tất cả dịch vụ',
        code: 'ALL',
      });

      return options;
    } catch (error) {
      console.error('Error fetching installments for autocomplete:', error);
      throw error;
    }
  },

  /**
   * Get installment by ID
   * @param {number} id - Installment ID
   * @returns {Promise<Object>} Installment data
   */
  async getById(id) {
    try {
      const response = await ApiService.get(`${cmdUrl.InstallmentsUrl.root}/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching installment with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create a new installment
   * @param {Object} payload - Installment data
   * @returns {Promise<Object>} Created installment response
   */
  async create(payload) {
    try {
      const response = await ApiService.post(cmdUrl.InstallmentsUrl.root, payload);
      return response.data;
    } catch (error) {
      console.error('Error creating installment:', error);
      throw error;
    }
  },

  /**
   * Update an existing installment
   * @param {number} id - Installment ID
   * @param {Object} data - Updated installment data
   * @returns {Promise<Object>} Updated installment response
   */
  async update(id, data) {
    try {
      const response = await ApiService.put(`${cmdUrl.InstallmentsUrl.root}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating installment with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete an installment
   * @param {number} id - Installment ID
   * @returns {Promise<Object>} Delete response
   */
  async delete(id) {
    try {
      const response = await ApiService.delete(`${cmdUrl.InstallmentsUrl.root}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting installment with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Search installments by name
   * @param {string} searchText - Search text
   * @param {number} limit - Maximum number of results
   * @returns {Promise<Array>} Filtered installment options
   */
  async search(searchText = '', limit = 200) {
    try {
      const options = await this.getForAutocomplete();
      
      if (!searchText.trim()) {
        return options.slice(0, limit);
      }

      const filtered = options.filter((item) => {
        return item.name.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
      });

      return filtered.slice(0, limit);
    } catch (error) {
      console.error('Error searching installments:', error);
      throw error;
    }
  },
};
